/**
 * Professional API Settings Dialog
 * 
 * Supports multiple LLM providers with clear separation:
 * - OpenAI (API key + default model)
 * - OpenRouter (API key + free-text model)
 * - Mistral AI (API key + default model)
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Info, Key, Sparkles, Check, AlertCircle } from 'lucide-react';

// Provider configurations with default models
const PROVIDER_CONFIG = {
  openai: {
    name: 'OpenAI',
    icon: '🤖',
    defaultModel: 'gpt-4o-mini',
    modelInfo: 'Recommandé pour le meilleur rapport qualité/prix',
    apiKeyPlaceholder: 'sk-...',
    docsUrl: 'https://platform.openai.com/api-keys',
    modelEditable: false, // Fixed model for simplicity
  },
  openrouter: {
    name: 'OpenRouter',
    icon: '🔀',
    defaultModel: 'openai/gpt-4o-mini',
    modelInfo: 'Modèle libre - tapez le nom exact du modèle voulu',
    apiKeyPlaceholder: 'sk-or-...',
    docsUrl: 'https://openrouter.ai/keys',
    modelEditable: true, // Free text input
  },
  mistral: {
    name: 'Mistral AI',
    icon: '⚡',
    defaultModel: 'mistral-small-latest',
    modelInfo: 'Rapide et économique pour les tâches courantes',
    apiKeyPlaceholder: '',
    docsUrl: 'https://console.mistral.ai/api-keys/',
    modelEditable: false, // Fixed model for simplicity
  },
};

const SettingsDialog = ({ open, onOpenChange, apiKey, setApiKey, selectedModel, setSelectedModel }) => {
  // Provider selection
  const [provider, setProvider] = useState(() => 
    sessionStorage.getItem('llm_provider') || 'openai'
  );
  
  // Provider-specific settings (stored separately)
  const [settings, setSettings] = useState({
    openai: {
      apiKey: sessionStorage.getItem('openai_api_key') || '',
      model: PROVIDER_CONFIG.openai.defaultModel,
    },
    openrouter: {
      apiKey: sessionStorage.getItem('openrouter_api_key') || '',
      model: sessionStorage.getItem('openrouter_model') || PROVIDER_CONFIG.openrouter.defaultModel,
    },
    mistral: {
      apiKey: sessionStorage.getItem('mistral_api_key') || '',
      model: PROVIDER_CONFIG.mistral.defaultModel,
    },
  });
  
  // Validation state
  const [errors, setErrors] = useState({});
  
  // Load active provider's settings into global state when provider changes
  useEffect(() => {
    if (settings[provider]) {
      setApiKey(settings[provider].apiKey);
      setSelectedModel(settings[provider].model);
    }
  }, [provider, settings, setApiKey, setSelectedModel]);
  
  const handleProviderChange = (newProvider) => {
    setProvider(newProvider);
    setErrors({}); // Clear errors when switching provider
  };
  
  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value
      }
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };
  
  const validateSettings = () => {
    const newErrors = {};
    const currentSettings = settings[provider];
    
    // API Key validation
    if (!currentSettings.apiKey || currentSettings.apiKey.trim() === '') {
      newErrors.apiKey = 'La clé API est requise';
    } else if (currentSettings.apiKey.length < 10) {
      newErrors.apiKey = 'La clé API semble trop courte';
    }
    
    // Model validation (for OpenRouter free text)
    if (provider === 'openrouter') {
      if (!currentSettings.model || currentSettings.model.trim() === '') {
        newErrors.model = 'Le nom du modèle est requis';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSave = () => {
    if (!validateSettings()) {
      toast.error('Veuillez corriger les erreurs avant de sauvegarder');
      return;
    }
    
    // Save current provider's settings to sessionStorage
    const currentSettings = settings[provider];
    sessionStorage.setItem(`${provider}_api_key`, currentSettings.apiKey);
    if (provider === 'openrouter') {
      sessionStorage.setItem('openrouter_model', currentSettings.model);
    }
    sessionStorage.setItem('llm_provider', provider);
    
    // Update global state
    setApiKey(currentSettings.apiKey);
    setSelectedModel(currentSettings.model);
    
    toast.success(`Configuration ${PROVIDER_CONFIG[provider].name} enregistrée`);
    onOpenChange(false);
  };
  
  const handleClear = () => {
    setSettings(prev => ({
      ...prev,
      [provider]: {
        apiKey: '',
        model: PROVIDER_CONFIG[provider].defaultModel,
      }
    }));
    setErrors({});
  };
  
  const currentConfig = PROVIDER_CONFIG[provider];
  const currentSettings = settings[provider];
  const hasApiKey = currentSettings.apiKey && currentSettings.apiKey.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-primary" />
            <DialogTitle>Configuration des API LLM</DialogTitle>
          </div>
          <DialogDescription>
            Configurez votre fournisseur d'IA pour activer le chat. Vos clés sont stockées localement et ne sont jamais envoyées à nos serveurs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Provider Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Fournisseur LLM</Label>
            <Tabs value={provider} onValueChange={handleProviderChange}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="openai" className="gap-2">
                  <span>{PROVIDER_CONFIG.openai.icon}</span>
                  OpenAI
                </TabsTrigger>
                <TabsTrigger value="openrouter" className="gap-2">
                  <span>{PROVIDER_CONFIG.openrouter.icon}</span>
                  OpenRouter
                </TabsTrigger>
                <TabsTrigger value="mistral" className="gap-2">
                  <span>{PROVIDER_CONFIG.mistral.icon}</span>
                  Mistral AI
                </TabsTrigger>
              </TabsList>

              {/* OpenAI Configuration */}
              <TabsContent value="openai" className="space-y-4 mt-4">
                <ProviderConfigForm
                  config={PROVIDER_CONFIG.openai}
                  settings={settings.openai}
                  errors={errors}
                  onChange={handleSettingChange}
                />
              </TabsContent>

              {/* OpenRouter Configuration */}
              <TabsContent value="openrouter" className="space-y-4 mt-4">
                <ProviderConfigForm
                  config={PROVIDER_CONFIG.openrouter}
                  settings={settings.openrouter}
                  errors={errors}
                  onChange={handleSettingChange}
                />
              </TabsContent>

              {/* Mistral Configuration */}
              <TabsContent value="mistral" className="space-y-4 mt-4">
                <ProviderConfigForm
                  config={PROVIDER_CONFIG.mistral}
                  settings={settings.mistral}
                  errors={errors}
                  onChange={handleSettingChange}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Status Indicator */}
          <Alert className={hasApiKey ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              {hasApiKey ? (
                <span className="text-green-700">
                  ✓ Clé API configurée pour {currentConfig.name}
                </span>
              ) : (
                <span className="text-amber-700">
                  Aucune clé API configurée. Le chat ne fonctionnera pas.
                </span>
              )}
            </AlertDescription>
          </Alert>

          {/* Security Notice */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <strong>Sécurité:</strong> Vos clés API sont stockées dans sessionStorage (effacées à la fermeture de l'onglet). 
              Pour une sécurité maximale en production, utilisez une gestion de sessions côté serveur.
            </AlertDescription>
          </Alert>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="outline" onClick={handleClear} size="sm">
            Effacer
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} className="gap-2">
              <Check className="w-4 h-4" />
              Enregistrer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Provider-specific configuration form
 */
const ProviderConfigForm = ({ config, settings, errors, onChange }) => {
  return (
    <div className="space-y-4">
      {/* Provider Info */}
      <div className="flex items-start gap-3 p-3 bg-accent/30 rounded-lg border border-accent">
        <Sparkles className="w-4 h-4 mt-0.5 text-primary" />
        <div className="flex-1 text-sm">
          <p className="font-medium mb-1">{config.name}</p>
          <p className="text-xs text-muted-foreground">
            Modèle par défaut: <code className="px-1 py-0.5 bg-muted rounded text-xs">{config.defaultModel}</code>
          </p>
          <p className="text-xs text-muted-foreground mt-1">{config.modelInfo}</p>
        </div>
      </div>

      {/* API Key Field */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${config.name}-api-key`} className="text-sm font-medium">
            Clé API
          </Label>
          <a
            href={config.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline flex items-center gap-1"
          >
            <Info className="w-3 h-3" />
            Obtenir une clé
          </a>
        </div>
        <Input
          id={`${config.name}-api-key`}
          type="password"
          placeholder={config.apiKeyPlaceholder}
          value={settings.apiKey}
          onChange={(e) => onChange('apiKey', e.target.value)}
          className={errors.apiKey ? 'border-destructive' : ''}
        />
        {errors.apiKey && (
          <p className="text-xs text-destructive">{errors.apiKey}</p>
        )}
      </div>

      {/* Model Field (conditional) */}
      {config.modelEditable ? (
        <div className="space-y-2">
          <Label htmlFor={`${config.name}-model`} className="text-sm font-medium">
            Nom du modèle
          </Label>
          <Input
            id={`${config.name}-model`}
            type="text"
            placeholder="ex: openai/gpt-4o, anthropic/claude-3.5-sonnet"
            value={settings.model}
            onChange={(e) => onChange('model', e.target.value)}
            className={errors.model ? 'border-destructive' : ''}
          />
          {errors.model && (
            <p className="text-xs text-destructive">{errors.model}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Tapez le nom exact du modèle. Consultez la{' '}
            <a href="https://openrouter.ai/models" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              liste des modèles
            </a>.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Modèle</Label>
          <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
            <Badge variant="secondary" className="font-mono text-xs">
              {settings.model}
            </Badge>
            <span className="text-xs text-muted-foreground">(Par défaut)</span>
          </div>
        </div>
      )}

      {/* Cost Info */}
      <div className="text-xs text-muted-foreground p-2 bg-muted/50 rounded">
        <strong>Coût:</strong> Variable selon le modèle et l'usage. 
        Consultez la tarification de {config.name} pour plus de détails.
      </div>
    </div>
  );
};

export default SettingsDialog;
